import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import {emailRegex, rand} from "../../../utils/function-util";
import * as _ from 'lodash';
import WarningIcon from '@material-ui/icons/Warning';
import GenericAssignmentError from "../../subscription-assignment/SubscriptionAssignmentPrompts/GenericAssignmentError";
import {addExternalProvider} from "../../../api/pro-guest-functions";
import {StyledInviteUserItem} from "../shared/user/InviteUserItem";
import {FormStatus, KeyValue} from "../../../models/shared";
import withEditorComponentStyle from "../../ui/styles/StyledEditorComponent";

const defaultItem = () => ({
    id: rand(),
    data: {firstName: '', lastName: '', email: ''},
    errors: {},
    status: FormStatus.none,
    correctiveAction: null
});

const ExternalProviderInviteEditor = ({
                                                 organizationId,
                                                 folderId,
                                                 onCancel,
                                                 onAdded,
                                                 onDone,
                                                 classes
                                             }: any) => {
    const {t, i18n} = useTranslation();
    const [forms, setForms] = React.useState([defaultItem()]);
    const [submitted, setSubmitted] = React.useState(false);

    const failedToAssignSub = (id: any, msg: any) => <GenericAssignmentError message={msg}
                                                                             onCancel={() => handleFormDeleted(id)}/>;
    const processing = forms.some(f => f.status === FormStatus.processing);
    const failedCount = forms.filter(f => f.status === FormStatus.failed).length;
    const allSuccessful = forms.every(f => f.status === FormStatus.successful);

    const addHandler = () => {
        setForms(newForms => ([...newForms, defaultItem()]))
    };

    const resetHandler = async (e: any) => {
        setForms([defaultItem()]);
        setSubmitted(false);
    };

    const submitForms = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setSubmitted(true);

        const validatedForms = forms.map(f => {
            const errors = ['email', 'firstName', 'lastName']
                .reduce((acc, name) => ({...acc, ...validateField(name, (f.data as any)[name])}), {});
            return {
                ...f,
                errors
            }
        });

        if (validatedForms.some(f => _.values(f.errors).some((v: any) => !!v)))
            return setForms(validatedForms);

        const formsToProcess = forms.filter(f => f.status !== FormStatus.successful && f.status !== FormStatus.processing);

        for (let i = 0; i < formsToProcess.length; i++) {
            await submitForm(formsToProcess[i].id);
        }

        if (onAdded) onAdded();
    };

    const submitForm = async (id: any) => {
        const userForm = forms.find(f => f.id === id);

        if(!userForm) return;

        updateForm(id, {status: FormStatus.processing});
        try {
            const provider = {
                ...userForm.data,
                language: i18n.language
            };

            await addExternalProvider(organizationId, folderId, provider)

            updateForm(id, {status: FormStatus.successful, correctiveAction: null});
        } catch (err) {
            const errorType = _.get(err, 'response.data.error.type') || 'UnknownError';
            let correctiveAction = null;
            const msg = `_Common.Api.Messages.${errorType}`;
            correctiveAction = failedToAssignSub(id, msg);
            updateForm(id, {status: FormStatus.failed, correctiveAction});
        }
    };

    const updateForm = (id: any, patch: any) => {
        setForms(newForms => {
            const index = newForms.findIndex(f => f.id === id);
            if (index <= -1) return newForms;
            const result = [...newForms];
            result[index] = _.merge({}, newForms[index], patch);
            return result;
        });
    };

    const handleFormChange = (name: any, value: any, form: any) => {
        const errors = validateField(name, value);
        const update = {
            data: {[name]: value},
            errors: errors
        };
        updateForm(form.id, update);
    };

    const handleFormDeleted = (id: any) => {
        setForms(formsForDelete => {
            const index = formsForDelete.findIndex(f => f.id === id);
            if (index <= -1) return formsForDelete;
            const newForms = [...formsForDelete];
            newForms.splice(index, 1);
            return newForms;
        });
    };

    const validateField = (field: any, value: any) => {
        const errors: KeyValue<string> = {};
        if (field === 'email') {
            errors[field] = emailRegex.test(value) ? null : 'EmailIsInvalid';
        }
        else {
            errors[field] = !value ? 'FieldIsRequired' : null;
        }
        return errors;
    };


    return <Fragment>
        {
            failedCount > 0 &&
            <Grid container className={classes.errorBanner} justify='center'>
                <WarningIcon/>
                <Typography variant="body2">
                    {t('Subscription.SubscriptionAssignment.XEntriesNeedAttention', {failedCount})}
                </Typography>
            </Grid>
        }
        <Grid container className={classes.formsContainer}>
            {
                forms.map((f, i) => <StyledInviteUserItem
                        form={f}
                        key={f.id.toString()}
                        handleFormDeleted={handleFormDeleted}
                        handleFormChange={handleFormChange}
                        submitted={submitted}>
                        {f.correctiveAction}
                    </StyledInviteUserItem>
                )
            }
        </Grid>
        <Grid container justify="flex-start">
            <IconButton aria-label="add" className={classes.addBtn} onClick={addHandler}>
                <AddIcon/>
            </IconButton>
        </Grid>
        <Grid container justify="flex-end" className={classes.actions}>
            {
                allSuccessful ?
                    <Button variant="contained" color="primary" size="medium"
                            onClick={onDone}
                            disabled={processing}>
                        {t('_Common.Labels.Done')}
                    </Button> :
                    <Fragment>
                        <Button variant="outlined" color="primary" size="medium"
                                onClick={onCancel}
                                disabled={processing}>
                            {t('_Common.Labels.Cancel')}
                        </Button>
                        <Button variant="outlined" color="primary" size="medium"
                                onClick={resetHandler}
                                disabled={processing}>
                            {t('_Common.Labels.Reset')}
                        </Button>
                        <Button variant="contained" color="primary" size="medium"
                                onClick={submitForms}
                                disabled={processing}>
                            {t('_Common.Labels.Add')}
                        </Button>
                    </Fragment>
            }
        </Grid>
    </Fragment>
};

export const StyleExternalProviderInviteEditor = withEditorComponentStyle(ExternalProviderInviteEditor) ;
